import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  XCircle,
  ArrowLeft,
  MessageSquare,
  RefreshCw,
  CreditCard,
  HelpCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const SubscriptionCancelPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Function to redirect to Stripe checkout
  const createCheckoutSession = async () => {
    try {
      // For simplicity, we'll redirect to plans page where user can select a plan
      // In a real scenario, you might want to redirect to a default plan
      navigate("/plans");
    } catch (error) {
      console.error("Error redirecting to checkout:", error);
      toast.error("Erreur lors de la redirection vers le paiement");
      // Fallback to plans page
      navigate("/plans");
    }
  };

  const reasons = [
    {
      icon: CreditCard,
      title: t("subscription.cancel.reasons.paymentIssue.title"),
      description: t("subscription.cancel.reasons.paymentIssue.description"),
      action: createCheckoutSession,
      actionText: t("subscription.cancel.reasons.paymentIssue.action"),
    },
    {
      icon: HelpCircle,
      title: t("subscription.cancel.reasons.changedMind.title"),
      description: t("subscription.cancel.reasons.changedMind.description"),
      action: () => navigate("/plans"),
      actionText: t("subscription.cancel.reasons.changedMind.action"),
    },
    {
      icon: MessageSquare,
      title: t("subscription.cancel.reasons.needsHelp.title"),
      description: t("subscription.cancel.reasons.needsHelp.description"),
      action: () =>
        toast.info(
          t("subscription.cancel.supportInfo", {
            supportEmail: import.meta.env.VITE_SUPPORT_EMAIL,
          }),
        ),
      actionText: t("subscription.cancel.reasons.needsHelp.action"),
    },
  ];

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      {/* Cancel Header */}
      <div className="text-center mb-12 space-y-6">
        {/* Cancel Icon with Animation */}
        <div className="relative">
          <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-r from-orange-100 to-red-100 dark:from-orange-900/30 dark:to-red-900/30 rounded-full flex items-center justify-center mx-auto">
            <XCircle className="w-12 h-12 sm:w-14 sm:h-14 text-orange-600 dark:text-orange-400" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 via-red-500/20 to-pink-500/20 blur-3xl -z-10"></div>
        </div>

        {/* Cancel Message */}
        <div className="space-y-4">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 dark:from-orange-400 dark:via-red-400 dark:to-pink-400 bg-clip-text text-transparent">
            {t("subscription.cancel.title")}
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            {t("subscription.cancel.subtitle")}
          </p>
          <Badge
            variant="outline"
            className="px-4 py-2 text-sm font-semibold text-emerald-600 dark:text-emerald-400"
          >
            {t("subscription.cancel.noCharges")}
          </Badge>
        </div>
      </div>

      {/* Common Reasons */}
      <Card className="max-w-4xl mx-auto mb-12">
        <CardHeader className="text-center">
          <CardTitle className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {t("subscription.cancel.commonReasonsTitle")}
          </CardTitle>
          <p className="text-gray-600 dark:text-gray-400">
            {t("subscription.cancel.commonReasonsDescription")}
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {reasons.map((reason, index) => (
              <div
                key={index}
                className="space-y-4 p-4 rounded-lg border border-gray-200 dark:border-gray-800 hover:shadow-md transition-shadow duration-300"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                    <reason.icon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  </div>
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {reason.title}
                  </h4>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {reason.description}
                </p>
                <Button variant="ghost" size="sm" onClick={reason.action}>
                  {reason.actionText}
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Help Section */}
      <Card className="max-w-2xl mx-auto bg-transparent border-0 dark:border-gray-800">
        <CardContent className="p-8 text-center space-y-6">
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t("subscription.cancel.helpTitle")}
            </h3>
            <p className="text-gray-600 dark:text-gray-300">
              {t("subscription.cancel.helpDescription")}
            </p>
          </div>

          <div className="space-y-1">
            <p className="text-sm text-gray-500 dark:text-gray-500">
              {t("subscription.cancel.contactEmail")}:{" "}
              <button
                className="text-violet-600 dark:text-violet-400 hover:underline"
                onClick={() => {
                  navigator.clipboard.writeText(
                    import.meta.env.VITE_SUPPORT_EMAIL,
                  );
                  toast.success(t("subscription.cancel.emailCopied"));
                }}
              >
                {import.meta.env.VITE_SUPPORT_EMAIL}
              </button>
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-600">
              {t("subscription.cancel.responseTime")}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="outline"
              onClick={() => navigate("/home")}
              className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 px-6 py-3"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t("subscription.cancel.backToHome")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionCancelPage;
